import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Database, DollarSign, Shield, Gauge, Clock, Skull,
  Search, Send, ArrowLeft, Check, Radar, HeartPulse, GitCompareArrows,
  Trash2, CreditCard, Monitor, BookOpen, ShieldCheck, AlertTriangle,
  ChevronRight, Activity, Eye, RefreshCw
} from "lucide-react";

/* ─── Design System Definitions ─── */
const DESIGNS = {
  fluent: {
    name: "Microsoft Fluent 2",
    description: "Microsoft's modern design system. Clean, layered surfaces with subtle depth, rounded corners, and the Segoe UI type system.",
    philosophy: "Layered depth, natural animations, familiar yet forward-looking. Used in Teams, Azure Portal, Windows 11.",
    font: "'Segoe UI Variable', 'Segoe UI', system-ui, sans-serif",
    monoFont: "'Cascadia Code', 'Consolas', monospace",
    radius: "8px",
    radiusSm: "6px",
    radiusLg: "12px",
    light: {
      bg: "#f5f5f5",
      surface: "#ffffff",
      surfaceAlt: "#fafafa",
      card: "#ffffff",
      text: "#242424",
      textSecondary: "#616161",
      textMuted: "#8a8a8a",
      accent: "#0078d4",
      accentLight: "#e8f4fd",
      accentHover: "#106ebe",
      border: "#e0e0e0",
      borderSubtle: "#f0f0f0",
      shadow: "0 2px 4px rgba(0,0,0,0.04), 0 0 2px rgba(0,0,0,0.06)",
      shadowHover: "0 4px 8px rgba(0,0,0,0.08), 0 0 2px rgba(0,0,0,0.06)",
      success: "#0e7a0d",
      warning: "#ca5010",
      danger: "#c50f1f",
      sidebarBg: "#f0f0f0",
      sidebarActive: "#e8f4fd",
      sidebarActiveText: "#0078d4",
      statusStrip: "#ffffff",
      inputBg: "#ffffff",
      inputBorder: "#d1d1d1",
      chipBg: "#f5f5f5",
      chipBorder: "#d1d1d1",
    },
    dark: {
      bg: "#1b1b1b",
      surface: "#2b2b2b",
      surfaceAlt: "#323232",
      card: "#2b2b2b",
      text: "#ffffff",
      textSecondary: "#c8c8c8",
      textMuted: "#8a8a8a",
      accent: "#4cc2ff",
      accentLight: "#0e3a58",
      accentHover: "#62cdff",
      border: "#404040",
      borderSubtle: "#383838",
      shadow: "0 2px 4px rgba(0,0,0,0.2)",
      shadowHover: "0 4px 8px rgba(0,0,0,0.3)",
      success: "#54b054",
      warning: "#f2a93b",
      danger: "#ff6b6b",
      sidebarBg: "#1f1f1f",
      sidebarActive: "#0e3a58",
      sidebarActiveText: "#4cc2ff",
      statusStrip: "#2b2b2b",
      inputBg: "#1f1f1f",
      inputBorder: "#505050",
      chipBg: "#383838",
      chipBorder: "#505050",
    },
  },
  material: {
    name: "Material Design 3",
    description: "Google's latest design language. Dynamic color, expressive shapes, and adaptive density. Emphasizes personality and warmth.",
    philosophy: "Expressive yet functional, with dynamic theming and bold elevation. Used across Google Workspace, Android.",
    font: "'Google Sans', 'Roboto Flex', 'Roboto', system-ui, sans-serif",
    monoFont: "'Roboto Mono', monospace",
    radius: "16px",
    radiusSm: "12px",
    radiusLg: "28px",
    light: {
      bg: "#fef7ff",
      surface: "#ffffff",
      surfaceAlt: "#f3edf7",
      card: "#ffffff",
      text: "#1c1b1f",
      textSecondary: "#49454f",
      textMuted: "#79747e",
      accent: "#6750a4",
      accentLight: "#eaddff",
      accentHover: "#7b61c4",
      border: "#cac4d0",
      borderSubtle: "#e7e0ec",
      shadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
      shadowHover: "0 4px 12px rgba(103,80,164,0.12)",
      success: "#006d3b",
      warning: "#7c5800",
      danger: "#ba1a1a",
      sidebarBg: "#f3edf7",
      sidebarActive: "#eaddff",
      sidebarActiveText: "#6750a4",
      statusStrip: "#f3edf7",
      inputBg: "#f3edf7",
      inputBorder: "#cac4d0",
      chipBg: "#f3edf7",
      chipBorder: "#cac4d0",
    },
    dark: {
      bg: "#141218",
      surface: "#1c1b1f",
      surfaceAlt: "#2b2930",
      card: "#211f26",
      text: "#e6e1e5",
      textSecondary: "#cac4d0",
      textMuted: "#938f99",
      accent: "#d0bcff",
      accentLight: "#332d41",
      accentHover: "#e0ccff",
      border: "#49454f",
      borderSubtle: "#36343b",
      shadow: "0 1px 3px rgba(0,0,0,0.3)",
      shadowHover: "0 4px 12px rgba(208,188,255,0.08)",
      success: "#6dd58c",
      warning: "#ffd95a",
      danger: "#ffb4ab",
      sidebarBg: "#1c1b1f",
      sidebarActive: "#332d41",
      sidebarActiveText: "#d0bcff",
      statusStrip: "#211f26",
      inputBg: "#2b2930",
      inputBorder: "#49454f",
      chipBg: "#2b2930",
      chipBorder: "#49454f",
    },
  },
  hig: {
    name: "Apple HIG",
    description: "Apple's Human Interface Guidelines. Premium feel with vibrancy, tight spacing, SF Pro typography, and iOS-native aesthetics.",
    philosophy: "Clarity, deference, depth. Minimal chrome, content-first. Used in macOS, iOS, iCloud, and Apple enterprise tools.",
    font: "-apple-system, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', system-ui, sans-serif",
    monoFont: "'SF Mono', 'Menlo', monospace",
    radius: "10px",
    radiusSm: "8px",
    radiusLg: "14px",
    light: {
      bg: "#f2f2f7",
      surface: "#ffffff",
      surfaceAlt: "#f2f2f7",
      card: "#ffffff",
      text: "#1c1c1e",
      textSecondary: "#3a3a3c",
      textMuted: "#8e8e93",
      accent: "#007aff",
      accentLight: "#e5f0ff",
      accentHover: "#0062cc",
      border: "#d1d1d6",
      borderSubtle: "#e5e5ea",
      shadow: "0 1px 3px rgba(0,0,0,0.04)",
      shadowHover: "0 2px 8px rgba(0,0,0,0.08)",
      success: "#34c759",
      warning: "#ff9500",
      danger: "#ff3b30",
      sidebarBg: "#f2f2f7",
      sidebarActive: "#e5f0ff",
      sidebarActiveText: "#007aff",
      statusStrip: "#ffffff",
      inputBg: "#ffffff",
      inputBorder: "#d1d1d6",
      chipBg: "#e5e5ea",
      chipBorder: "#d1d1d6",
    },
    dark: {
      bg: "#000000",
      surface: "#1c1c1e",
      surfaceAlt: "#2c2c2e",
      card: "#1c1c1e",
      text: "#ffffff",
      textSecondary: "#d1d1d6",
      textMuted: "#8e8e93",
      accent: "#0a84ff",
      accentLight: "#0d2137",
      accentHover: "#409cff",
      border: "#38383a",
      borderSubtle: "#2c2c2e",
      shadow: "none",
      shadowHover: "0 2px 8px rgba(0,0,0,0.3)",
      success: "#30d158",
      warning: "#ff9f0a",
      danger: "#ff453a",
      sidebarBg: "#1c1c1e",
      sidebarActive: "#0d2137",
      sidebarActiveText: "#0a84ff",
      statusStrip: "#1c1c1e",
      inputBg: "#2c2c2e",
      inputBorder: "#38383a",
      chipBg: "#2c2c2e",
      chipBorder: "#48484a",
    },
  },
};

const navItems = [
  { label: "Insights", icon: Sparkles, active: true },
  { label: "Resources", icon: Database },
  { label: "Cost Intelligence", icon: DollarSign },
  { label: "Drift & Changes", icon: GitCompareArrows },
  { label: "Cloud Hygiene", icon: HeartPulse },
  { label: "Cleanup Wizard", icon: Trash2 },
];

const settingsItems = [
  { label: "Billing", icon: CreditCard },
  { label: "System", icon: Monitor },
  { label: "Docs", icon: BookOpen },
  { label: "Admin", icon: ShieldCheck },
];

/* ─── Mini Preview Component ─── */
function DesignPreview({ designKey, mode }) {
  const design = DESIGNS[designKey];
  const t = design[mode];
  const f = design.font;
  const mf = design.monoFont;
  const r = design.radius;
  const rSm = design.radiusSm;
  const rLg = design.radiusLg;

  return (
    <div
      style={{ background: t.bg, color: t.text, fontFamily: f, borderRadius: "12px", overflow: "hidden", border: `1px solid ${t.border}` }}
      className="h-full flex"
      data-testid={`preview-${designKey}-${mode}`}
    >
      {/* Sidebar */}
      <div style={{ width: "200px", background: t.sidebarBg, borderRight: `1px solid ${t.border}`, flexShrink: 0 }} className="flex flex-col">
        {/* Logo */}
        <div style={{ padding: "16px 16px 12px", borderBottom: `1px solid ${t.borderSubtle}` }} className="flex items-center gap-2.5">
          <div style={{ width: "32px", height: "32px", borderRadius: rSm, background: t.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Radar style={{ width: "16px", height: "16px", color: mode === "dark" ? t.bg : "#fff" }} />
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: 700, letterSpacing: "-0.01em" }}>Niyant</div>
            <div style={{ fontSize: "9px", color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.1em" }}>Azure Discovery</div>
          </div>
        </div>

        {/* Nav Items */}
        <div style={{ padding: "8px", flex: 1 }}>
          <div style={{ fontSize: "9px", fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", padding: "8px 8px 4px" }}>Overview</div>
          {navItems.slice(0, 1).map((item) => (
            <div key={item.label} style={{
              display: "flex", alignItems: "center", gap: "8px", padding: "7px 10px", borderRadius: rSm,
              background: item.active ? t.sidebarActive : "transparent",
              color: item.active ? t.sidebarActiveText : t.textSecondary,
              fontSize: "13px", fontWeight: item.active ? 600 : 400,
              border: item.active ? `1px solid ${t.accent}22` : "1px solid transparent",
              marginBottom: "2px",
            }}>
              <item.icon style={{ width: "14px", height: "14px" }} />
              {item.label}
            </div>
          ))}
          <div style={{ fontSize: "9px", fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", padding: "12px 8px 4px" }}>Explore</div>
          {navItems.slice(1, 5).map((item) => (
            <div key={item.label} style={{
              display: "flex", alignItems: "center", gap: "8px", padding: "7px 10px", borderRadius: rSm,
              color: t.textSecondary, fontSize: "13px", marginBottom: "1px",
            }}>
              <item.icon style={{ width: "14px", height: "14px" }} />
              {item.label}
            </div>
          ))}
          <div style={{ fontSize: "9px", fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", padding: "12px 8px 4px" }}>Actions</div>
          {navItems.slice(5).map((item) => (
            <div key={item.label} style={{
              display: "flex", alignItems: "center", gap: "8px", padding: "7px 10px", borderRadius: rSm,
              color: t.textSecondary, fontSize: "13px", marginBottom: "1px",
            }}>
              <item.icon style={{ width: "14px", height: "14px" }} />
              {item.label}
            </div>
          ))}
        </div>

        {/* User */}
        <div style={{ padding: "12px", borderTop: `1px solid ${t.borderSubtle}` }} className="flex items-center gap-2">
          <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: `${t.accent}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: 700, color: t.accent }}>B</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "12px", fontWeight: 500 }}>Billing Test</div>
            <div style={{ fontSize: "10px", color: t.textMuted }}>billing_test@test.com</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: "24px 28px", overflow: "hidden" }}>
        {/* Greeting */}
        <div style={{ marginBottom: "20px" }}>
          <h1 style={{ fontSize: "18px", fontWeight: 600, letterSpacing: "-0.01em", margin: 0 }}>Good evening, Billing</h1>
          <p style={{ fontSize: "11px", color: t.textMuted, marginTop: "2px" }}>Saturday, Feb 28</p>
        </div>

        {/* Status Strip */}
        <div style={{
          display: "flex", gap: "20px", flexWrap: "wrap", padding: "12px 16px",
          background: t.statusStrip, borderRadius: r, border: `1px solid ${t.border}`,
          boxShadow: t.shadow, marginBottom: "20px",
        }}>
          {[
            { icon: Shield, label: "Security", value: "Critical", color: t.danger },
            { icon: Gauge, label: "Efficiency", value: "92%", color: t.accent },
            { icon: Database, label: "Resources", value: "927", color: t.accent },
            { icon: Skull, label: "Orphans", value: "104", color: t.warning },
            { icon: DollarSign, label: "Spend", value: "$135k", color: t.success },
            { icon: Clock, label: "Last Scan", value: "413m", color: t.textMuted },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-2">
              <div style={{ width: "28px", height: "28px", borderRadius: rSm, background: `${stat.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <stat.icon style={{ width: "13px", height: "13px", color: stat.color }} />
              </div>
              <div>
                <div style={{ fontSize: "13px", fontWeight: 700, fontFamily: mf, color: stat.color, lineHeight: 1 }}>{stat.value}</div>
                <div style={{ fontSize: "9px", color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Command Bar */}
        <div style={{
          display: "flex", alignItems: "center", gap: "8px",
          background: t.inputBg, border: `1px solid ${t.inputBorder}`, borderRadius: r,
          padding: "0 12px", marginBottom: "8px", boxShadow: t.shadow,
        }}>
          <Search style={{ width: "14px", height: "14px", color: t.textMuted, flexShrink: 0 }} />
          <div style={{ flex: 1, padding: "10px 0", fontSize: "13px", color: t.textMuted }}>
            Ask anything... e.g. 'which subscription needs attention?'
          </div>
          <div style={{ width: "28px", height: "28px", borderRadius: rSm, background: `${t.accent}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Send style={{ width: "12px", height: "12px", color: t.accent }} />
          </div>
        </div>

        {/* Chips */}
        <div style={{ display: "flex", gap: "6px", marginBottom: "20px", flexWrap: "wrap" }}>
          {["24 Drift Alerts", "Clean 104 Orphans", "Cost Analysis", "Governance Report"].map((chip) => (
            <div key={chip} style={{
              padding: "4px 10px", borderRadius: rLg, fontSize: "10px", fontWeight: 500,
              color: t.textSecondary, background: t.chipBg, border: `1px solid ${t.chipBorder}`,
            }}>{chip}</div>
          ))}
        </div>

        {/* Section Label */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
          <Sparkles style={{ width: "12px", height: "12px", color: t.accent }} />
          <span style={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em", color: t.textSecondary }}>Today's Briefing</span>
          <div style={{ flex: 1, height: "1px", background: t.borderSubtle }} />
          <div className="flex items-center gap-1" style={{ fontSize: "10px", color: t.textMuted }}>
            <RefreshCw style={{ width: "10px", height: "10px" }} /> Refresh
          </div>
        </div>

        {/* Insight Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
          {[
            { title: "Recurring Auth Failures: TC_Tan", severity: "HIGH", color: t.danger, icon: Shield, desc: "Application 'aec31502...' is failing authentication repeatedly. This may indicate expired credentials.", action: "Review Access Logs" },
            { title: "$11,365/mo in Orphaned Resources", severity: "MEDIUM", color: t.warning, icon: DollarSign, desc: "We are spending over $11k on 104 unattached resources. High-cost items include unattached disks.", action: "View Orphans" },
            { title: "Recent Bulk Deletions Detected", severity: "INFO", color: t.accent, icon: Activity, desc: "A significant cleanup of Commvault snapshots occurred ~7 hours ago. Verify these deletions were intentional.", action: "Check Change Log" },
          ].map((card, i) => (
            <div key={i} style={{
              background: t.surfaceAlt, border: `1px solid ${card.color}15`, borderRadius: r,
              overflow: "hidden", boxShadow: t.shadow,
            }}>
              <div style={{ height: "2px", background: `linear-gradient(90deg, ${card.color}50, ${card.color}10, transparent)` }} />
              <div style={{ padding: "14px" }}>
                <div className="flex items-center justify-between" style={{ marginBottom: "10px" }}>
                  <div style={{ width: "28px", height: "28px", borderRadius: rSm, background: `${card.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <card.icon style={{ width: "13px", height: "13px", color: card.color }} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: card.color }} />
                    <span style={{ fontSize: "8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: card.color }}>{card.severity}</span>
                  </div>
                </div>
                <h3 style={{ fontSize: "12px", fontWeight: 600, lineHeight: 1.3, marginBottom: "6px" }}>{card.title}</h3>
                <p style={{ fontSize: "10px", color: t.textSecondary, lineHeight: 1.5 }}>{card.desc}</p>
                <div className="flex items-center gap-1" style={{ marginTop: "10px", fontSize: "10px", fontWeight: 600, color: card.color }}>
                  {card.action} <ChevronRight style={{ width: "10px", height: "10px" }} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Meta insight */}
        <div style={{
          display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px",
          background: t.surface, border: `1px solid ${t.border}`, borderRadius: r,
          marginTop: "12px",
        }}>
          <Eye style={{ width: "12px", height: "12px", color: `${t.accent}66`, flexShrink: 0 }} />
          <p style={{ fontSize: "10px", color: t.textMuted, lineHeight: 1.5 }}>
            Despite a massive cleanup of snapshots today, your monthly cost trend has still risen 23% since September.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function DesignPreviewPage() {
  const navigate = useNavigate();
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [previewMode, setPreviewMode] = useState("light");

  if (selectedDesign) {
    const design = DESIGNS[selectedDesign];
    return (
      <div className="space-y-6" data-testid="design-fullview">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setSelectedDesign(null)} className="h-8 w-8" data-testid="back-to-comparison">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">{design.name}</h1>
              <p className="text-[11px] text-[var(--n-text-muted)] mt-0.5">{design.philosophy}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg overflow-hidden border border-[var(--n-border-2)]">
              <button
                onClick={() => setPreviewMode("light")}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${previewMode === "light" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="mode-light"
              >
                Light
              </button>
              <button
                onClick={() => setPreviewMode("dark")}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${previewMode === "dark" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="mode-dark"
              >
                Dark
              </button>
            </div>
            <Button
              onClick={() => navigate("/")}
              className="h-8 px-4 text-xs font-semibold bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="choose-design-btn"
            >
              <Check className="w-3.5 h-3.5 mr-1.5" /> Choose this Design
            </Button>
          </div>
        </div>

        {/* Full Preview */}
        <div style={{ height: "calc(100vh - 200px)", minHeight: "500px" }}>
          <DesignPreview designKey={selectedDesign} mode={previewMode} />
        </div>

        {/* Design Details */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-[var(--n-surface)] border border-[var(--n-border)]">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Typography</p>
            <p className="text-sm" style={{ fontFamily: design.font }}>{design.font.split(",")[0].replace(/'/g, "")}</p>
            <p className="text-xs text-muted-foreground mt-1" style={{ fontFamily: design.monoFont }}>Mono: {design.monoFont.split(",")[0].replace(/'/g, "")}</p>
          </div>
          <div className="p-4 rounded-xl bg-[var(--n-surface)] border border-[var(--n-border)]">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Corner Radius</p>
            <div className="flex items-center gap-3">
              <div style={{ width: "40px", height: "40px", borderRadius: design.radiusSm, border: `2px solid ${design[previewMode].accent}` }} />
              <div style={{ width: "40px", height: "40px", borderRadius: design.radius, border: `2px solid ${design[previewMode].accent}` }} />
              <div style={{ width: "40px", height: "40px", borderRadius: design.radiusLg, border: `2px solid ${design[previewMode].accent}` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-2">{design.radiusSm} / {design.radius} / {design.radiusLg}</p>
          </div>
          <div className="p-4 rounded-xl bg-[var(--n-surface)] border border-[var(--n-border)]">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Accent Palette</p>
            <div className="flex items-center gap-2">
              <div style={{ width: "32px", height: "32px", borderRadius: "6px", background: design[previewMode].accent }} />
              <div style={{ width: "32px", height: "32px", borderRadius: "6px", background: design[previewMode].success }} />
              <div style={{ width: "32px", height: "32px", borderRadius: "6px", background: design[previewMode].warning }} />
              <div style={{ width: "32px", height: "32px", borderRadius: "6px", background: design[previewMode].danger }} />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Primary, Success, Warning, Danger</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1200px] mx-auto" data-testid="design-preview-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Design System Preview</h1>
          <p className="text-[11px] text-[var(--n-text-muted)] mt-0.5">
            Compare how Niyant would look with different design systems. Click any to see a full preview.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-[var(--n-border-2)]">
            <button
              onClick={() => setPreviewMode("light")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${previewMode === "light" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="compare-mode-light"
            >
              Light
            </button>
            <button
              onClick={() => setPreviewMode("dark")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${previewMode === "dark" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="compare-mode-dark"
            >
              Dark
            </button>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-xs text-muted-foreground" data-testid="back-to-app">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to App
          </Button>
        </div>
      </div>

      {/* Design Cards */}
      {Object.entries(DESIGNS).map(([key, design]) => (
        <div key={key} className="space-y-3" data-testid={`design-option-${key}`}>
          {/* Design Name + Description */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold">{design.name}</h2>
              <p className="text-[11px] text-[var(--n-text-muted)] mt-0.5 max-w-xl">{design.description}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedDesign(key)}
              className="text-xs border-[var(--n-border-2)] hover:border-cyan-500/40 hover:text-cyan-600"
              data-testid={`expand-${key}`}
            >
              Full Preview <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>

          {/* Preview */}
          <div
            className="cursor-pointer transition-all duration-300 hover:shadow-lg"
            style={{ height: "400px", borderRadius: "12px", overflow: "hidden" }}
            onClick={() => setSelectedDesign(key)}
          >
            <DesignPreview designKey={key} mode={previewMode} />
          </div>
        </div>
      ))}
    </div>
  );
}
